import { Config, ContextEngine, CustomContext } from '@/types';
import { __FieldContextHelpers } from './field-context-helpers';
import { isEuropeanCountry } from './format';
import { DEFAULT_WIDGET_ICON } from '@/components/ui/WidgetButton/default-icon';

// Tracking constants
export const TRACKING_KEYS = {
  IMPACT_CODE: 'impactCode',
  GCLID_CODE: 'GCLID',
  MSCLKID_CODE: 'MSCLKID',
  GBRAID_CODE: 'GBRAID',
  WBRAID_CODE: 'WBRAID',
  FBCLID_CODE: 'FBCLID',
  LI_FAT_ID_CODE: 'LI_FAT_ID',
  RDT_CID_CODE: 'RDT_CID',
  DCLID_CODE: 'DCLID',
  UTM_SOURCE: 'utmSource',
  UTM_MEDIUM: 'utmMedium',
  UTM_CAMPAIGN: 'utmCampaign',
  UTM_TERM: 'utmTerm',
  UTM_CONTENT: 'utmContent',
  // Single atomic last-touch record for all attribution parameters
  ATTRIBUTION_RECORD: 'attribution',
} as const;

// Expiry for the atomic attribution record: 90 days in milliseconds
export const TRACKING_EXPIRY_MS = 90 * 24 * 60 * 60 * 1000;

// Default field configurations
export const DEFAULT_FIELD_CONFIGS: Config.Field[] = [
  {
    name: 'name',
    title: 'Name',
    label: 'What is your name?',
    type: 'text',
    enabled: true,
    required: true,
    placeholder: 'Name',
  },
  {
    name: 'email',
    title: 'Business Email',
    label: 'Business Email',
    type: 'email',
    enabled: true,
    required: true,
    placeholder: 'your@company.com',
  },
  {
    name: 'phone',
    title: 'Phone',
    label: 'What is your phone number?',
    type: 'tel',
    enabled: true,
    required: true,
    placeholder: 'Phone',
  },
  {
    name: 'topic',
    title: 'Topic',
    label: "What's the topic?",
    type: 'text',
    enabled: true,
    required: true,
    placeholder: 'e.g., Leadership, Innovation, Wellbeing',
  },
  {
    name: 'lookingForSpecify',
    title: 'Specify',
    label: 'Please specify what you are looking for',
    type: 'text',
    enabled: true,
    required: true,
    placeholder: 'Please specify',
    dependsOn: {
      name: 'lookingFor',
      value: 'Other',
    },
  },
  {
    name: 'date',
    title: 'Date',
    label: 'When is your event?',
    unsureCheckboxText: "I'm not sure yet",
    type: 'date',
    enabled: true,
    required: true,
    placeholder: 'Select date',
  },
  {
    name: 'eventType',
    title: 'Event Type',
    label: 'What is the format of your event?',
    type: 'radio',
    enabled: true,
    required: true,
    options: ['In person', 'Virtual', 'Not sure'],
  },
  {
    name: 'location',
    title: 'Location',
    label: 'Where is your event taking place?',
    type: 'text',
    enabled: true,
    required: true,
    placeholder: 'City, Country',
    dependsOn: {
      name: 'eventType',
      value: 'In person',
    },
  },
  {
    name: 'budget',
    title: 'Budget',
    label: "What's your budget range for speaker fees?",
    type: 'radio',
    enabled: true,
    required: true,
    // UK default options (£)
    options: [
      'Under £2k',
      '£2k - £5k',
      '£5k - £10k',
      '£10k - £25k',
      '£25k - £50k',
      '£50k - £100k',
      '£100k+',
      'Not sure yet',
    ],
    // Dynamic options based on detected country code
    dynamicOptions: (
      _formValues: Record<string, string>,
      context: __FieldContextHelpers
    ) => {
      const countryCode = context.userCountryCode?.toUpperCase() || 'GB';

      // USA ($)
      if (countryCode === 'US') {
        return [
          'Under $2k',
          '$2k - $5k',
          '$5k - $10k',
          '$10k - $25k',
          '$25k - $50k',
          '$50k - $100k',
          '$100k+',
          'Not sure yet',
        ];
      }

      // Europe (€) - excluding UK which uses GBP
      if (isEuropeanCountry(countryCode) && countryCode !== 'GB') {
        return [
          'Under €2k',
          '€2k - €5k',
          '€5k - €10k',
          '€10k - €25k',
          '€25k - €50k',
          '€50k - €100k',
          '€100k+',
          'Not sure yet',
        ];
      }

      // UK and rest of world default (£) - return undefined to use default options
      return undefined;
    },
  },
  {
    name: 'details',
    title: 'Event Details',
    label: 'Tell us more about your event',
    type: 'textarea',
    enabled: true,
    required: false,
    placeholder:
      'Tell us more about your event: audience size, audience type, theme, key topics, etc.',
    rows: 4,
  },
];

// Parse avatar URLs from env (comma-separated, configurable via CI/CD)
const envAvatarUrls = (process.env.WIDGET_AVATAR_URLS || '')
  .split(',')
  .map(url => url.trim())
  .filter(Boolean);

const DEFAULT_AVATARS =
  envAvatarUrls.length > 0
    ? envAvatarUrls.map((url, i) => ({
        url,
        alt: `Support Agent ${i + 1}`,
        width: '49px',
        height: '49px',
      }))
    : [
        {
          url: 'https://rdbnckowwosjlixwhvoe.supabase.co/storage/v1/object/public/profile-pictures/93ad9c44-0d63-40c5-b962-211f06f1ef32/profile-1765535034072.webp',
          alt: 'Support Agent 1',
          width: '49px',
          height: '49px',
        },
        {
          url: 'https://rdbnckowwosjlixwhvoe.supabase.co/storage/v1/object/public/profile-pictures/c1a168c1-c492-40d5-93de-35f19add0653/profile-1766145576931.jpeg',
          alt: 'Support Agent 2',
          width: '49px',
          height: '49px',
        },
      ];

// Default theme configuration with modern design system
export const DEFAULT_THEME: Config.Theme = {
  // Color system with modern palette
  colors: {
    primary: '#3970c0', // Primary/Dusty Blue
    secondary: '#ffffff', // Primary/White
    neutrals: {
      primary: {
        900: '#F5F8FC', // Neutrals/Pale Blue/Pale Blue (900)
        '900-disabled': '#f5f5f5',
        850: '#E1EAF6', // Neutrals/Pale Blue/Pale Blue (850)
        750: '#CDDBEF', // Neutrals/Pale Blue/Pale Blue (750)
      },
      secondary: {
        900: '#ECECED', // Neutrals/Pale Grey/Pale Gray (900)
        750: '#CFD0D2', // Neutrals/Pale Grey/Pale Gray (750)
      },
    },
    surface: '#f8fafc', // Slate-50
    text: {
      primary: '#2a2b32', // Primary/Dark Gray
      secondary: '#64748b', // Slate-500
      tertiary: '#5C5E62', // Slate-600
      disabled: '#cbd5e1', // Slate-300
      inverse: '#ffffff', // White
    },
    border: {
      primary: '#3970c0', // Indigo-600
      secondary: '#f1f5f9', // Slate-100
      focus: '#3970c0', // Primary color
      hover: '#d1d5db', // Neutrals/Pale Blue/Pale Blue (900)
    },
    status: {
      success: '#10b981', // Emerald-500
      warning: '#f59e0b', // Amber-500
      error: '#ef4444', // Red-500
      info: '#3b82f6',
    },
    chat: {
      userBubble: '#3970c0', // Primary color
      agentBubble: '#F7F7F7', // Slate-100
      systemBubble: '#e0f2fe', // Sky-100
    },
  },

  spacing: {
    xs: '6px',
    sm: '10px',
    md: '15px',
    lg: '20px',
    xl: '24px',
    xxl: '32px',
    xxxl: '35px',
  },

  // Modern typography system (aligned with Intercom)
  typography: {
    fontFamily: {
      primary:
        '-apple-system, Sofia Pro, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      secondary:
        'Recoleta Alt, Georgia, Cambria, "Times New Roman", Times, serif',
      mono: 'ui-monospace, SFMono-Regular, "Roboto Mono", "Menlo", Monaco, monospace',
    },
    fontSize: {
      xs: '12px', // Smaller text (previously 12px)
      sm: '13px', // Small text (previously 14px)
      base: '14px', // Body text - aligned with Intercom (previously 16px)
      lg: '16px', // Larger text (previously 18px)
      xl: '18px', // Extra large (previously 20px)
      '2xl': '20px', // 2XL (previously 24px)
      '3xl': '30px', // 3XL (previously 24px)
    },
    fontWeight: {
      light: 300,
      normal: 300,
      medium: 500,
      semibold: 600,
      bold: 500,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },

  // Border radius system
  borderRadius: {
    none: '0px',
    sm: '4px',
    base: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    '2xl': '30px',
    max: '999px',
    full: '50%',
  },

  // Soft shadow system for depth and layering
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.05)',
  },

  // Smooth transitions
  transitions: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    base: '250ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '350ms cubic-bezier(0.4, 0, 0.2, 1)',
    slower: '500ms cubic-bezier(0.4, 0, 0.2, 1)',
  },

  // Z-index system
  zIndex: {
    base: 1,
    elevated: 10,
    sticky: 100,
    overlay: 1000,
    modal: 1100,
    popover: 1200,
    tooltip: 1300,
    toast: 1400,
  },

  // Branding configuration
  branding: {
    showBranding: true,
    companyName: '',
    tagline: '',
    logo: {
      url: `https://${process.env.CLOUDFRONT_DOMAIN}/images/logo-peptalk.png`, // URL to your company logo (loads from CloudFront CDN)
      alt: 'Company Logo',
      width: '124px',
      height: '32px',
      position: 'left', // 'left', 'center', or 'right'
    },
    avatars: DEFAULT_AVATARS,
  },
};

// Default configuration with comprehensive theme
export const DEFAULT_CONFIG: Config.WidgetBase = {
  theme: DEFAULT_THEME,
  context: {},
  contactEmail: 'hello@getapeptalk.com',
  behavior: {
    autoOpen: false,
    closeOnClickOutside: true,
    enableSound: false,
  },
  features: {
    enableHomeTab: false, // Feature flag: set to true to enable home tab
    home: {
      header: {
        title: 'Hi there!',
        subtitle: 'How can I help you today?',
      },
      chatActionButton: {
        title: 'Chat with us',
        description: 'We typically reply in a few minutes',
        titleWhenCompleted: 'Start New Inquiry',
        descriptionWhenCompleted: 'We typically reply in a few minutes',
      },
      linkButton: {
        title: 'Book a meeting',
        description: "We'd love talking to you",
        url: 'https://www.getapeptalk.com/',
      },
    },
    chat: {
      welcomeMessage: (
        context: ContextEngine.ContextResult | null,
        customContext: CustomContext | null
      ) => {
        // ProposalConversation state - resuming existing conversation
        if (customContext?.proposalId) {
          const title = customContext.proposalTitle as string | undefined;
          return title
            ? `Hi 👋 Continuing our conversation about "${title}". How can I help?`
            : `Hi 👋 Continuing our conversation. How can I help?`;
        }

        // NewInquiry state - standard welcome messages
        if (context?.pageType === 'expert' && context.extracted?.expertName) {
          return `Hi 👋 Interested in working with ${context.extracted?.expertName}? Tell us more about what you're looking for, and we'd be happy to share their fees and availability.`;
        }
        if (customContext?.topic) {
          return `Hi 👋 Interested in ${customContext.topic}? Tell us what you're looking for, and we'd be happy to recommend the perfect fit.`;
        }
        return `Hi 👋 Tell us what you're looking for, and we'd be happy to recommend the perfect match for you.`;
      },
      confirmMessage:
        "Thanks for reaching out! The team's got your request and will get straight to work on it - you'll hear from them very soon.\nNeed something sooner? Press continue, and you'll be connected to someone on chat.",
      errorMessage:
        "Oops! We're having trouble processing your request right now. Please try again in a moment, or if the problem persists, you can email us directly at {{contactEmail}} and we'll get back to you as soon as possible.",
      skipQuestionnaire: false,
      submitButton: {
        text: 'Send',
        loadingText: 'Sending...',
      },
      agentName: 'PepTalk',
      fields: DEFAULT_FIELD_CONFIGS,
    },
  },
  ui: {
    position: 'bottom-right' as const,
    size: 'medium' as const,
    hoveringMessage: 'Talk to us! 👋',
    floatingMessageDelay: 2400,
    floatingMessageAutoHideDelay: 10000,
    showFloatingMessage: true,
    showBubble: true,
    icon: DEFAULT_WIDGET_ICON,
    target: undefined,
    formOnly: false,
  },
  customComponents: {
    // Text
    Text: null,
    // Avatar
    Avatar: null,
    // Avatars
    Avatars: null,
    AvatarsContainer: null,
    AvatarsItem: null,
    // WidgetButton
    WidgetButton: null,
    WidgetButtonRoot: null,
    WidgetButtonIconContainer: null,
    WidgetButtonIcon: null,
    WidgetButtonCloseIcon: null,
    // TypingIndicator
    TypingIndicator: null,
    TypingIndicatorContainer: null,
    TypingIndicatorAvatar: null,
    TypingIndicatorDots: null,
    // FloatingMessage
    FloatingMessage: null,
    FloatingMessageContainer: null,
    FloatingMessageContent: null,
    FloatingMessageText: null,
    FloatingMessageArrow: null,
    // OfflineMessage
    OfflineMessage: null,
    OfflineMessageContainer: null,
    OfflineMessageEmoji: null,
    OfflineMessageTitleText: null,
    OfflineMessageSubtitleText: null,
    OfflineMessageEmailLink: null,
    // ActionCard
    ActionCard: null,
    ActionCardRoot: null,
    ActionCardContent: null,
    ActionCardTitle: null,
    ActionCardDescription: null,
    ActionCardIcon: null,
    // Header
    Header: null,
    HeaderContainer: null,
    HeaderRoot: null,
    HeaderContent: null,
    HeaderLogo: null,
    HeaderCloseButton: null,
    HeaderTitles: null,
    // ToggleView
    ToggleView: null,
    ToggleViewContainer: null,
    ToggleViewOption: null,
    ToggleViewIcon: null,
    ToggleViewLabel: null,
    // ChatMessage
    ChatMessage: null,
    ChatMessageContainer: null,
    ChatMessageContent: null,
    ChatMessageMeta: null,
    ChatMessageText: null,
    // ChatInput
    ChatInput: null,
    ChatInputContainer: null,
    ChatInputTextarea: null,
    ChatInputSendButton: null,
    ChatInputIcon: null,
    // Widget Container
    WidgetContainer: null,
    // Home Container
    HomeContainer: null,
    // Chat Container
    ChatContainer: null,
    // Questionnaire Container
    QuestionnaireContainer: null,
    QuestionnaireSubmitButton: null,
    QuestionnaireSubmitLoading: null,
    QuestionnaireSubmitIcon: null,
    QuestionnaireSubmitText: null,
    // Date
    Date: null,
    DateContainer: null,
    DateInput: null,
    DateUnsureCheckbox: null,
    // FormGroup
    FormGroup: null,
    FormGroupContainer: null,
    FormGroupLabel: null,
    FormGroupError: null,
    // Input
    Input: null,
    // Select
    Select: null,
    // Textarea
    Textarea: null,
    // PhoneInput
    PhoneInput: null,
    PhoneInputContainer: null,
    PhoneInputCountrySelect: null,
    PhoneInputCountryDropdown: null,
    PhoneInputNumberInput: null,
    // Button
    Button: null,
    // Radio
    Radio: null,
    RadioContainer: null,
    RadioItem: null,
    RadioButton: null,
    RadioInput: null,
  },
  // TODO: Make GTM containerId configurable per customer instead of hardcoding
  gtm: {
    enabled: true,
    containerId: 'GTM-NWLGKMH5',
    loadIfMissing: true,
  },
};
